// NearMeGeoBlock — personalised header for the 6 Near Me pillar pages.
// Uses IP geolocation (no permission prompt) and surfaces the user's city +
// nearest existing Anand Tutorials city/state landing page. Falls back to a
// generic Pan-India card if geo is unavailable.

import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { useIpGeo } from "../lib/useIpGeo";
import { V7_STATES_LIST, V7_TIER2_CITY_LIST } from "../data/indiaProgrammaticPages";

// Existing metro / core city slugs already live on the site (from extendedCities).
const EXISTING_CITY_SLUGS = {
    jabalpur: { path: "/jabalpur" },
    bhopal: { path: "/bhopal" },
    nagpur: { path: "/nagpur" },
    mumbai: { path: "/online-tuition-mumbai" },
    delhi: { path: "/online-tuition-delhi" },
    bangalore: { path: "/online-tuition-bangalore" },
    pune: { path: "/online-tuition-pune" },
    hyderabad: { path: "/online-tuition-hyderabad" },
    chennai: { path: "/online-tuition-chennai" },
    kolkata: { path: "/online-tuition-kolkata" },
    ahmedabad: { path: "/online-tuition-ahmedabad" },
    indore: { path: "/online-tuition-indore" },
};

function _resolveCityLink(userCity) {
    if (!userCity) return null;
    const norm = userCity.toLowerCase().trim();
    // 1. Try existing metro / core cities (highest authority pages)
    for (const [slug, v] of Object.entries(EXISTING_CITY_SLUGS)) {
        if (norm === slug || norm.startsWith(slug)) return { name: userCity, path: v.path };
    }
    // 2. Try Tier-2 / Tier-3 cities (V7 batch)
    const t2 = V7_TIER2_CITY_LIST.find((c) => c.slug === norm || norm.startsWith(c.slug));
    if (t2) return { name: t2.name, path: t2.path };
    return null;
}

function _resolveStateLink(userRegion) {
    if (!userRegion) return null;
    const norm = userRegion.toLowerCase().trim().replace(/\s+/g, "-").replace(/&/g, "and");
    const s = V7_STATES_LIST.find((s) => s.slug === norm || norm.includes(s.slug) || s.slug.includes(norm));
    return s ? { name: s.name, path: s.path } : null;
}

export default function NearMeGeoBlock() {
    const geo = useIpGeo();
    if (!geo || geo.countryCode === "IN" && !geo.city && !geo.region) {
        // Fallback: minimal "Pan-India" card
        return (
            <div data-testid="near-me-geo-block" className="bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white rounded-2xl p-6 md:p-8 mb-10">
                <div className="text-xs font-semibold tracking-[0.3em] uppercase text-blue-300 mb-2">Coverage</div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Active in every Indian city, town & pin code</h2>
                <p className="text-blue-100 mb-4">From Tier-1 metros to Tier-3 towns. The tutor lives anywhere in India; the class is in your child's room via Zoom or Google Meet.</p>
                <div className="flex flex-wrap gap-2">
                    <Link to="/online-tuition-india-cities" className="bg-white/10 hover:bg-white/20 transition-colors text-white text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2">Browse all cities <ArrowRight className="w-4 h-4" /></Link>
                    <Link to="/online-tuition-india-states" className="bg-white/10 hover:bg-white/20 transition-colors text-white text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2">Browse all states <ArrowRight className="w-4 h-4" /></Link>
                </div>
            </div>
        );
    }

    const cityLink = _resolveCityLink(geo.city);
    const stateLink = _resolveStateLink(geo.region);

    return (
        <div data-testid="near-me-geo-block" className="bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white rounded-2xl p-6 md:p-8 mb-10 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl"></div>
            <div className="relative">
                <div className="text-xs font-semibold tracking-[0.3em] uppercase text-blue-300 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> {geo.country === "India" ? "Tutors near you" : "Available in your country"}
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">
                    {geo.city ? `1:1 online tuition in ${geo.city}, ${geo.region || ""}` : `1:1 online tuition across ${geo.country || "India"}`}
                </h2>
                <p className="text-blue-100 mb-5 max-w-2xl">
                    {geo.country === "India"
                        ? `Anand Tutorials matches families in ${geo.city || geo.region || "your area"} with verified 1:1 Indian online tutors in 24-48 hours. Live two-way HD video. Recorded for revision. Weekly Sunday parent reports.`
                        : `Anand Tutorials runs dedicated NRI / international programs for Indian-origin and foreign students worldwide. Find your country page below.`}
                </p>
                <div className="flex flex-wrap gap-2">
                    {cityLink && (
                        <Link to={cityLink.path} data-testid="geo-city-link" className="bg-white text-[#0F172A] text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-2 hover:bg-blue-50 transition-colors">
                            Open the {cityLink.name} page <ArrowRight className="w-4 h-4" />
                        </Link>
                    )}
                    {stateLink && (
                        <Link to={stateLink.path} data-testid="geo-state-link" className="bg-white/10 hover:bg-white/20 transition-colors text-white text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2">
                            Tutors across {stateLink.name} <ArrowRight className="w-4 h-4" />
                        </Link>
                    )}
                    {!cityLink && !stateLink && (
                        <Link to="/online-tuition-india-cities" className="bg-white text-[#0F172A] text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-2 hover:bg-blue-50 transition-colors">
                            Browse all cities <ArrowRight className="w-4 h-4" />
                        </Link>
                    )}
                    <Link to="/contact" className="bg-amber-400 text-[#0F172A] text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-2 hover:bg-amber-300 transition-colors">
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
